// Feature flags per controllare funzionalità dell'applicazione
const FEATURE_FLAG_OVERRIDES_KEY = 'ef_feature_flag_overrides';

const DEFAULT_FEATURE_FLAGS = {
  // SearchPage flags
  SHOW_FILTERS: false,           // Nasconde i filtri nella pagina di ricerca
  SHOW_REGIONS: true,            // Mostra il box delle regioni
  // Header flags
  SHOW_LOGIN_REGISTER: true,     // Mostra i link "Accedi" e "Registrati"
  SHOW_POST_AD: false,           // Mostra il pulsante "Carica Annuncio"
  // Utils
  LOG: false,                    // Abilita i log di debug
  // FG (Fingerprinting) flags
  FG_ENABLED: true,              // Abilita il servizio di fingerprinting
  FG_COUNTRY_FILTER: false,      // Abilita il filtro per paese
  FG_BOT: false,                 // Abilita l'invio dei dati per i bot (i bot sono sempre consentiti)
  FG_CACHE: true,                // Abilita la cache dei dati fg
} as const;

export type FeatureFlagKey = keyof typeof DEFAULT_FEATURE_FLAGS;
type FeatureFlags = { [K in FeatureFlagKey]: boolean };
type FeatureFlagOverrides = Partial<FeatureFlags>;

function isClient(): boolean {
  return typeof window !== 'undefined' && typeof localStorage !== 'undefined';
}

function loadOverrides(): FeatureFlagOverrides {
  if (!isClient()) return {};

  try {
    const raw = localStorage.getItem(FEATURE_FLAG_OVERRIDES_KEY);
    if (!raw) return {};

    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const validOverrides: FeatureFlagOverrides = {};

    for (const key of Object.keys(DEFAULT_FEATURE_FLAGS) as FeatureFlagKey[]) {
      const value = parsed[key];
      if (typeof value === 'boolean') {
        validOverrides[key] = value;
      }
    }

    return validOverrides;
  } catch {
    return {};
  }
}

function persistOverrides(overrides: FeatureFlagOverrides): void {
  if (!isClient()) return;

  try {
    localStorage.setItem(FEATURE_FLAG_OVERRIDES_KEY, JSON.stringify(overrides));
  } catch {
    // ignore storage errors (private mode/quota)
  }
}

function removeOverrides(): void {
  if (!isClient()) return;

  try {
    localStorage.removeItem(FEATURE_FLAG_OVERRIDES_KEY);
  } catch {
    // ignore storage errors
  }
}

export const FEATURE_FLAGS: FeatureFlags = {
  ...DEFAULT_FEATURE_FLAGS,
  ...loadOverrides(),
};

// Espone i flag su window per Greasemonkey/Tampermonkey
if (isClient()) {
  (window as unknown as Record<string, unknown>)['__EF_FLAGS__'] = FEATURE_FLAGS;
}

const logDebug = (...args: unknown[]): void => {
  console.log(...args);
};

export function setFeatureFlag<K extends FeatureFlagKey>(key: K, value: FeatureFlags[K]): void {
  FEATURE_FLAGS[key] = value;
  persistOverrides(getFeatureFlagOverrides());
}


export function getFeatureFlagOverrides(): FeatureFlagOverrides {
  const overrides: FeatureFlagOverrides = {};

  for (const key of Object.keys(DEFAULT_FEATURE_FLAGS) as FeatureFlagKey[]) {
    if (FEATURE_FLAGS[key] !== DEFAULT_FEATURE_FLAGS[key]) {
      overrides[key] = FEATURE_FLAGS[key];
    }
  }

  return overrides;
}

export function resetFeatureFlags(): void {
  for (const key of Object.keys(DEFAULT_FEATURE_FLAGS) as FeatureFlagKey[]) {
    FEATURE_FLAGS[key] = DEFAULT_FEATURE_FLAGS[key];
  }
  removeOverrides();
}

export function getDefaultFeatureFlags(): FeatureFlags {
  return { ...DEFAULT_FEATURE_FLAGS };
}

export type FeatureFlagsConsoleApi = {
  list: () => FeatureFlags;
  get: (key: FeatureFlagKey) => boolean;
  set: (key: FeatureFlagKey, value: boolean) => void;
  defaults: () => FeatureFlags;
  reset: () => void;
  help: () => void;
};

function installFeatureFlagsConsoleApi(): void {
  if (!isClient()) return;

  (window as Window & { efFlags?: FeatureFlagsConsoleApi }).efFlags = {
    list: () => {
      const current = {...FEATURE_FLAGS};
      console.table(current);
      return current;
    },
    get: (key) => FEATURE_FLAGS[key],
    set: (key, value) => {
      setFeatureFlag(key, value);
      logDebug(`[efFlags] ${key} => ${value}`);
    },
    defaults: () => getDefaultFeatureFlags(),
    reset: () => {
      resetFeatureFlags();
      logDebug('[efFlags] override resettati ai valori di default');
    },
    help: () => {
      logDebug('efFlags API:');
      logDebug(' - efFlags.list()');
      logDebug(' - efFlags.get("SHOW_FILTERS")');
      logDebug(' - efFlags.set("SHOW_FILTERS", true)');
      logDebug(' - efFlags.defaults()');
      logDebug(' - efFlags.reset()');
    },
  };
}

installFeatureFlagsConsoleApi();

export default FEATURE_FLAGS;
